import { US_STATE_CODES } from '../enums';
import { registerReportDefs, reportColumn as c } from './reports';
import type { ReportColumn, ReportDef, ReportFilter } from './reports';

/**
 * List screens of the sidebar that share the reports engine: a heading, the reference's
 * filters and columns, and a data provider on the server. Row actions are added by the
 * client page that shows the screen.
 *
 * Filters and columns come from the product inventory (appendix parts 1 and 2). Where the
 * reference's columns were not read, the definition says so; columns this application has no
 * data for are left out.
 */

/* ---------------------------------- Ports --------------------------------- */

export const PORTIN_STATUSES = ['Pending', 'Failed', 'Cancelled', 'Completed'] as const;
export type PortInStatus = (typeof PORTIN_STATUSES)[number];

/** Request types of the reference's Portin History filter. */
export const PORT_REQUEST_TYPES = [
  'NewPortin',
  'ExistingSubscriberNewEsn',
  'ExistingSubscriberOldEsn',
  'CancelPortin',
  'UpdatePortin',
  'QueryPortin',
  'ValidatePortin',
] as const;
export type PortRequestType = (typeof PORT_REQUEST_TYPES)[number];

/** Networks the reference has port-out lists for; a port-out belongs to one of them. */
export const PORTOUT_NETWORKS = ['T-Mobile', 'Plintron(ITG)', 'Plum'] as const;
export type PortOutNetwork = (typeof PORTOUT_NETWORKS)[number];
export const PORTOUT_STATUSES = ['Pending', 'Completed', 'Rejected'] as const;
export type PortOutStatus = (typeof PORTOUT_STATUSES)[number];

const PORT_FILTERS: ReportFilter[] = [
  { type: 'carrier', key: 'carrier', label: 'Carrier' },
  { type: 'text', key: 'mdn', label: 'MDN', maxLength: 10 },
  { type: 'text', key: 'sim', label: 'SIM', maxLength: 25 },
  { type: 'dateRange', key: 'date', label: 'Start To End Date' },
];

const PORT_PERSON = [c('firstName', 'First name'), c('lastName', 'Last name')];
const PORT_LINE = [
  c('mdn', 'MDN'),
  c('newSim', 'New SIM'),
  c('carrier', 'Carrier'),
  c('source', 'Source'),
];

const portList = (
  key: string,
  heading: string,
  code: string,
  columns: ReportColumn[],
  filters: ReportFilter[] = PORT_FILTERS,
): ReportDef => ({
  key,
  group: 'screen',
  name: heading,
  heading,
  code,
  filters,
  columns,
  submitLabel: 'Search',
  autoRun: true,
  resultHeading: null,
});

const PORT_SCREENS: ReportDef[] = [
  portList('portin-pending', 'Pending portin list', 'PC394', [
    ...PORT_PERSON,
    c('customerId', 'Customer id'),
    c('enrollmentId', 'Enrollment id'),
    ...PORT_LINE,
    c('requestedAt', 'Request date time', 'datetime'),
    c('attemptCount', 'Attempt count', 'number'),
    c('oldCarrier', 'Old carrier'),
    c('carrierResponse', 'Carrier response'),
    c('respondedAt', 'Response date time', 'datetime'),
    c('isEsim', 'Is eSIM', 'yesno'),
  ]),
  portList(
    'portin-manual',
    'Portin queue list',
    'PC394',
    [
      ...PORT_PERSON,
      c('customerId', 'Customer id'),
      c('enrollmentId', 'Enrollment id'),
      c('mdn', 'MDN'),
      c('newSim', 'New SIM'),
      c('imei', 'IMEI'),
      c('carrier', 'Carrier'),
      c('source', 'Source'),
      c('requestedAt', 'Request date time', 'datetime'),
    ],
    [
      ...PORT_FILTERS,
      {
        type: 'select',
        key: 'requestStatus',
        label: 'Request status',
        options: ['Pending', 'Cancel', 'Failed'],
        emptyLabel: 'All',
      },
    ],
  ),
  portList('portin-failed', 'Failed portin list', 'PC395', [
    ...PORT_PERSON,
    c('enrollmentId', 'Enrollment id'),
    ...PORT_LINE,
    c('respondedAt', 'Date time', 'datetime'),
  ]),
  portList('portin-cancelled', 'Cancel portin list', 'PC395', [
    ...PORT_PERSON,
    c('enrollmentId', 'Enrollment id'),
    ...PORT_LINE,
    c('requestedAt', 'Request date time', 'datetime'),
    c('cancelledAt', 'Cancel date time', 'datetime'),
  ]),
  portList('portin-completed', 'Completed portin list', 'PC396', [
    ...PORT_PERSON,
    c('enrollmentId', 'Enrollment id'),
    ...PORT_LINE,
    c('requestedAt', 'Request date time', 'datetime'),
    c('completedAt', 'Completed date time', 'datetime'),
  ]),
  portList('portin-swaps', 'Pending Port-in Swaps', 'PC343', [
    ...PORT_PERSON,
    c('customerId', 'Customer id'),
    c('enrollmentId', 'Enrollment id'),
    c('oldMdn', 'Old MDN'),
    c('oldSim', 'Old SIM'),
    ...PORT_LINE,
    c('requestedAt', 'Request date time', 'datetime'),
    c('carrierResponse', 'Carrier response'),
    c('respondedAt', 'Response date', 'datetime'),
  ]),
  portList(
    'portin-history',
    'Portin history',
    'PC778',
    [
      c('sim', 'SIM'),
      c('mdn', 'MDN'),
      c('carrier', 'Carrier'),
      c('requestType', 'Request type'),
      c('status', 'Status'),
      c('at', 'Action date', 'datetime'),
      c('source', 'Source'),
    ],
    [
      { type: 'text', key: 'mdn', label: 'MDN', maxLength: 10 },
      { type: 'text', key: 'sim', label: 'SIM', maxLength: 25 },
      {
        type: 'select',
        key: 'requestType',
        label: 'Request type',
        options: PORT_REQUEST_TYPES,
        emptyLabel: 'All',
      },
    ],
  ),
];

// The reference's port-out columns were not read; these are the fields a port-out has here.
const PORTOUT_COLUMNS = [
  ...PORT_PERSON,
  c('customerId', 'Customer id'),
  c('mdn', 'MDN'),
  c('sim', 'SIM'),
  c('gainingCarrier', 'New carrier'),
  c('requestedAt', 'Request date time', 'datetime'),
];
const MDN_FILTER: ReportFilter = { type: 'text', key: 'mdn', label: 'MDN', maxLength: 10 };

const PORTOUT_SCREENS: ReportDef[] = [
  portList(
    'portout-tmobile-completed',
    'T-Mobile Completed Portout List',
    'PC815',
    [...PORTOUT_COLUMNS, c('completedAt', 'Completed date time', 'datetime')],
    [{ type: 'text', key: 'sim', label: 'ESN', maxLength: 25 }, MDN_FILTER],
  ),
  portList('portout-itg-pending', 'Plintron(ITG) Pending Portout List', 'PC923', PORTOUT_COLUMNS, [
    MDN_FILTER,
  ]),
  portList(
    'portout-itg-completed',
    'Plintron(ITG) Completed Portout List',
    'PC924',
    [...PORTOUT_COLUMNS, c('completedAt', 'Completed date time', 'datetime')],
    [MDN_FILTER],
  ),
  portList('portout-plum-pending', 'Plum Pending Portout List', 'PC937', PORTOUT_COLUMNS, [
    MDN_FILTER,
  ]),
  portList(
    'portout-plum-completed',
    'Plum Completed Portout List',
    'PC936',
    [...PORTOUT_COLUMNS, c('completedAt', 'Completed date time', 'datetime')],
    [MDN_FILTER],
  ),
];

/** Which network and status each port-out list shows. */
export const PORTOUT_LISTS: Record<string, { network: PortOutNetwork; status: PortOutStatus }> = {
  'portout-tmobile-completed': { network: 'T-Mobile', status: 'Completed' },
  'portout-itg-pending': { network: 'Plintron(ITG)', status: 'Pending' },
  'portout-itg-completed': { network: 'Plintron(ITG)', status: 'Completed' },
  'portout-plum-pending': { network: 'Plum', status: 'Pending' },
  'portout-plum-completed': { network: 'Plum', status: 'Completed' },
};

/* --------------------------------- Shipment -------------------------------- */

/** Statuses of the reference's Shipping Queue filter; this build uses New, Assigned, Shipped, Cancel. */
export const SHIPPING_STATUSES = [
  'New',
  'Shipped',
  'Assigned',
  'Print Label Pending',
  'Provision Fail',
  'Nlad Fail',
  'Cancel',
] as const;
export type ShippingStatus = 'New' | 'Assigned' | 'Shipped' | 'Cancel';

/** Shippers of the reference's scan dialog. */
export const SHIPPERS = ['UPS', 'USPS', 'FEDEX', 'ENDICIA', 'STAMPS', 'NONE', 'SHIPST'] as const;
export type Shipper = (typeof SHIPPERS)[number];

/** The reference has 20 shipping queues; prepaid and postpaid orders use this one. */
export const SHIPPING_QUEUES = ['Prepaid Order'] as const;

const SHIPMENT_SCREENS: ReportDef[] = [
  {
    key: 'shipping-queue',
    group: 'screen',
    name: 'Shipping Queue',
    heading: 'Shipping Queue',
    code: 'PC1066',
    filters: [
      {
        type: 'select',
        key: 'queue',
        label: 'Queue Name',
        options: SHIPPING_QUEUES,
        emptyLabel: 'ALL',
      },
      { type: 'state', key: 'state', label: 'State', allLabel: 'All' },
      { type: 'agentChain' },
      { type: 'text', key: 'trackingNumber', label: 'Tracking Number', maxLength: 40 },
      { type: 'dateRange', key: 'date', label: 'Created Date Range' },
      {
        type: 'select',
        key: 'status',
        label: 'Status',
        options: SHIPPING_STATUSES,
        emptyLabel: 'All',
      },
      { type: 'text', key: 'q', label: 'Search Order', maxLength: 60 },
      {
        type: 'select',
        key: 'paid',
        label: 'Payment Completed',
        options: ['Yes', 'No'],
        emptyLabel: 'All',
      },
    ],
    columns: [
      c('enrollmentId', 'Enrollment Id'),
      c('firstName', 'First Name'),
      c('lastName', 'Last Name'),
      c('contactNumber', 'Contact Number'),
      c('email', 'Email'),
      c('mailing1', 'Mailing Add1'),
      c('mailing2', 'Mailing Add2'),
      c('mailingCity', 'Mailing City'),
      c('state', 'State'),
      c('mailingZip', 'Mailing Zip'),
      c('planName', 'Plan Name'),
      c('agentName', 'Agent Name'),
      c('queue', 'Queue Name'),
      c('approvedAt', 'Approve Date Time', 'datetime'),
      c('enrollmentType', 'Enrollment Type'),
      c('requestedModel', 'Requested Model'),
      c('trackingNumber', 'Tracking Number'),
      c('status', 'Shipment Status'),
      c('shippedAt', 'Shipment Date', 'datetime'),
      c('masterAgent', 'Master Agent'),
      c('distributor', 'Distributor'),
      c('retailer', 'Retailer'),
      c('employee', 'Employee'),
      c('paid', 'Payment Completed', 'yesno'),
      c('paymentAmount', 'Payment Amount', 'money'),
      c('source', 'Source'),
    ],
    submitLabel: 'Search',
    autoRun: true,
    resultHeading: null,
  },
  {
    key: 'shipment-dashboard',
    group: 'screen',
    name: 'Shipment Dashboard',
    heading: 'Shipment Dashboard',
    code: 'PC223',
    filters: [
      { type: 'agentChain' },
      { type: 'text', key: 'trackingNumber', label: 'Tracking Number', maxLength: 40 },
      {
        type: 'select',
        key: 'status',
        label: 'Status',
        options: ['Scanned', 'Pending'],
        emptyLabel: 'All',
      },
      { type: 'state', key: 'state', label: 'State', allLabel: 'All' },
      { type: 'dateRange', key: 'date', label: 'Created Date Range' },
      { type: 'text', key: 'enrollmentId', label: 'Enrollment ID', maxLength: 20 },
      { type: 'dateRange', key: 'shipped', label: 'Shipment Date' },
    ],
    columns: [
      c('enrollmentId', 'Enrollment ID'),
      c('customerId', 'Completed ID'),
      c('firstName', 'First Name'),
      c('lastName', 'Last Name'),
      c('sim', 'SIM'),
      c('mdn', 'MDN'),
      c('deviceId', 'Device ID'),
      c('state', 'State'),
      c('mailingCity', 'City'),
      c('mailingZip', 'Zip'),
      c('mailing1', 'Mailing Address 1'),
      c('mailing2', 'Mailing Address 2'),
      c('scanStatus', 'Status'),
      c('shipper', 'Shipper'),
      c('trackingNumber', 'Tracking Number'),
      c('masterAgent', 'Master'),
      c('distributor', 'Distributor'),
      c('retailer', 'Retailer'),
      c('employee', 'Employee'),
      c('createdAt', 'Created Date', 'datetime'),
      c('shippedAt', 'Shipment Completion Date', 'datetime'),
      c('accountStatus', 'Activation'),
    ],
    submitLabel: 'Search',
    autoRun: true,
    resultHeading: 'Shipment Order summary',
  },
  {
    key: 'equipment-requests',
    group: 'screen',
    name: 'Equipment & Accessories Request',
    heading: 'Equipment & Accessories Request',
    code: 'PC538',
    filters: [
      {
        type: 'select',
        key: 'status',
        label: 'Status',
        options: ['Pending Order', 'Completed Order'],
        required: true,
      },
      { type: 'text', key: 'customerId', label: 'Customer Id', maxLength: 11 },
      {
        type: 'select',
        key: 'processType',
        label: 'Process Type',
        options: ['Resend Phone', 'Equipment Purchase'],
        emptyLabel: 'All',
      },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
    ],
    // The reference's result columns load after its search and were not read.
    columns: [
      c('customerId', 'Customer Id'),
      c('customerName', 'Customer Name'),
      c('equipment', 'Equipment & Accessories'),
      c('equipmentType', 'Equipment Type'),
      c('processType', 'Process Type'),
      c('reason', 'Reason'),
      c('amount', 'Amount', 'money'),
      c('status', 'Status'),
      c('requestedAt', 'Request Date', 'datetime'),
      c('completedAt', 'Completed Date', 'datetime'),
      c('trackingNumber', 'Tracking Number'),
    ],
    submitLabel: 'Search',
  },
];

/* ---------------------------------- Review --------------------------------- */

export const REVIEW_STATUSES = ['Pending Audit', 'Rejected', 'Approved'] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

// The reference's result columns were not read; these are the order's own fields.
const REVIEW_COLUMNS = [
  c('enrollmentId', 'Enroll ID'),
  c('customerId', 'Customer ID'),
  c('firstName', 'First Name'),
  c('lastName', 'Last Name'),
  c('state', 'State'),
  c('zip', 'Zip Code'),
  c('planName', 'Plan Name'),
  c('source', 'Source'),
  c('orderDate', 'Order Date', 'datetime'),
  c('orderStatus', 'Order Status'),
  c('reason', 'Reason'),
  c('decidedBy', 'Reviewed By'),
  c('decidedAt', 'Reviewed Date', 'datetime'),
];

/**
 * The three lists of Pending Orders on the reference (walk of 2026-10-23), behind the switch
 * `fraud-review-tabs`. The owner's split (2026-10-24): Live Orders are the orders of agents and
 * of the API, Website the website's, Telesales Orders the CSRs'. Assumption: a CSR is a user of
 * the administrator level. Sources this application never records match nothing.
 */
export const REVIEW_LIVE_SOURCES = [
  'AgentProgram',
  'API',
  'POS',
  'Tablet',
  'Fasttrack',
  'Enrollment Portal',
] as const;
export const REVIEW_WEBSITE_SOURCES = ['Agent', 'Website'] as const;

const reviewTab = (key: string, name: string, sources: readonly string[] | null): ReportDef => ({
  key,
  group: 'screen',
  name,
  heading: 'Fraud Orders Review Queue',
  code: 'PC984',
  feature: 'fraud-review-tabs',
  filters: [
    { type: 'dateRange', key: 'date', label: 'Date Range' },
    {
      type: 'select',
      key: 'orderStatus',
      label: 'Order Status',
      options: REVIEW_STATUSES,
      emptyLabel: 'All Orders',
    },
    { type: 'select', key: 'states', label: 'State(s)', options: US_STATE_CODES, multiple: true },
    ...(sources
      ? [
          {
            type: 'select' as const,
            key: 'sources',
            label: 'Source(s)',
            options: sources,
            multiple: true,
          },
        ]
      : []),
  ],
  columns: REVIEW_COLUMNS,
  submitLabel: 'Apply',
  autoRun: true,
  resultHeading: null,
});

const REVIEW_SCREENS: ReportDef[] = [
  reviewTab('fraud-review-live', 'Live Orders', REVIEW_LIVE_SOURCES),
  reviewTab('fraud-review-website', 'Website', REVIEW_WEBSITE_SOURCES),
  reviewTab('fraud-review-telesales', 'Telesales Orders', null),
  {
    key: 'fraud-review-pending',
    group: 'screen',
    name: 'Pending Orders',
    heading: 'Fraud Orders Review Queue',
    code: 'PC984',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Date' },
      {
        type: 'select',
        key: 'orderStatus',
        label: 'Order Status',
        options: REVIEW_STATUSES,
        emptyLabel: 'All Orders',
      },
      { type: 'state', key: 'state', label: 'State', allLabel: 'All' },
      {
        type: 'select',
        key: 'source',
        label: 'Source',
        options: ['BSS', 'WEBSITE', 'API'],
        emptyLabel: 'All',
      },
    ],
    columns: REVIEW_COLUMNS,
    submitLabel: 'Search',
    autoRun: true,
    resultHeading: null,
  },
  {
    key: 'fraud-review-lookup',
    group: 'screen',
    name: 'Order Lookup',
    heading: 'Fraud Orders Review Queue',
    code: 'PC984',
    filters: [
      { type: 'text', key: 'enrollmentId', label: 'Enroll ID', maxLength: 20 },
      { type: 'text', key: 'customerId', label: 'Customer ID', maxLength: 11 },
      { type: 'text', key: 'mdn', label: 'MDN', maxLength: 10 },
      { type: 'text', key: 'firstName', label: 'First Name', maxLength: 40 },
      { type: 'text', key: 'lastName', label: 'Last Name', maxLength: 40 },
      {
        type: 'text',
        key: 'subscriberId',
        label: 'NLAD Subscriber ID',
        maxLength: 30,
        feature: 'review-nlad-subscriber-id',
      },
      { type: 'state', key: 'state', label: 'State', allLabel: 'All' },
      { type: 'text', key: 'zip', label: 'Zip Code', maxLength: 5 },
    ],
    columns: REVIEW_COLUMNS,
    submitLabel: 'Search',
    resultHeading: null,
  },
];

/* ------------------------------ Customer Service --------------------------- */

/** Payment types of the reference's Payment Search Tool; this build takes Cash only. */
export const PAYMENT_SEARCH_TYPES = [
  'Credit Card',
  'Cash',
  'Lock Box',
  'Check By Phones',
  'Money Order',
  'Fi Server',
  'Ace Cash Express',
] as const;

const CUSTOMER_SERVICE_SCREENS: ReportDef[] = [
  {
    key: 'payment-search',
    group: 'screen',
    name: 'Payment Search Tool',
    heading: 'Payment Search Tool',
    code: 'PC339',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },

      {
        type: 'select',
        key: 'searchType',
        label: 'Search Type',
        options: ['Amount', 'Confirmation', 'CC# (Last 4 Digits)'],
        emptyLabel: 'All',
      },
      { type: 'text', key: 'text', label: 'Search', maxLength: 40 },
      { type: 'dateRange', key: 'date', label: 'Date' },
      {
        type: 'select',
        key: 'paymentType',
        label: 'Payment Type',
        options: PAYMENT_SEARCH_TYPES,
        emptyLabel: 'All',
      },
    ],
    // The reference's result columns load after its search and were not read.
    columns: [
      c('customerId', 'Customer ID'),
      c('customerName', 'Customer Name'),
      c('mdn', 'MDN'),
      c('totalPaid', 'Amount', 'money'),
      c('paymentMethod', 'Payment Type'),
      c('receiptNumber', 'Confirmation'),
      c('invoiceNo', 'Invoice No'),
      c('paidAt', 'Payment Date', 'datetime'),
      c('postedBy', 'Posted By'),
      c('paymentStatus', 'Status'),
    ],
    submitLabel: 'Search',
  },
];

/* ----------------------------- Inventory reports --------------------------- */

const UNIT_STATUS_FILTER: ReportFilter = {
  type: 'select',
  key: 'status',
  label: 'Status',
  options: ['Free', 'Used', 'Bad', 'Removed', 'Fraud', 'Lost/Stolen'],
  emptyLabel: 'All',
};
// The columns of the Inventory Dashboard's unit list.
const UNIT_COLUMNS = [
  c('carrier', 'Carrier'),
  c('status', 'Status'),
  c('product', 'Product'),
  c('storeType', 'Store Type'),
  c('store', 'Store ID'),
  c('mdn', 'MDN'),
  c('poNumber', 'PO'),
  c('uploadedAt', 'Uploaded', 'datetime'),
];

const inventoryReport = (
  key: string,
  name: string,
  heading: string,
  code: string,
  filters: ReportFilter[],
  columns: ReportColumn[],
): ReportDef => ({
  key,
  group: 'screen',
  name,
  heading,
  code,
  filters,
  columns,
  submitLabel: 'Search',
  autoRun: true,
  resultHeading: name,
});

const UNIT_FILTERS: ReportFilter[] = [
  { type: 'carrier', key: 'carrier', label: 'Carrier' },
  UNIT_STATUS_FILTER,
  { type: 'agentChain' },
  { type: 'dateRange', key: 'date', label: 'Uploaded Date' },
];

const INVENTORY_SCREENS: ReportDef[] = [
  inventoryReport(
    'inventory-sim-report',
    'SIM Inventory Reports',
    'Inventory Report',
    'PC350',
    UNIT_FILTERS,
    [c('identifier', 'SIM / ESN'), c('isEsim', 'Is eSIM', 'yesno'), ...UNIT_COLUMNS],
  ),
  inventoryReport(
    'inventory-device-report',
    'Device Inventory Reports',
    'Inventory Report',
    'PC350',
    UNIT_FILTERS,
    [c('identifier', 'IMEI'), ...UNIT_COLUMNS],
  ),
  // The walk of 2026-10-23 (release switch `inventory-reports-2`).
  ...(['sim', 'device'] as const).map((kind) => ({
    ...inventoryReport(
      kind === 'sim' ? 'agent-sim-inventory' : 'agent-device-inventory',
      kind === 'sim' ? 'SIM Inventory for Agents' : 'GSM Device Inventory for Agents (PC214)',
      kind === 'sim' ? 'SIM Inventory for Agents' : 'GSM Device Inventory for Agents (PC214)',
      kind === 'sim' ? 'PC215' : 'PC214',
      [{ type: 'agentChain' }, { type: 'dateRange', key: 'date', label: 'Date Range' }],
      [
        c('masterAgent', 'Master Agent Name'),
        c('distributor', 'Distributor Name'),
        c('retailer', 'Retailer Name'),
        c('employee', 'Employee Name'),
        c('box', 'Box ID'),
        c('assignTo', 'Assign To'),
        c('assignBy', 'Assign By'),
        c('assignedAt', 'Inventory Assigned Date', 'datetime'),
        c('city', 'City'),
        c('state', 'State'),
        c('zip', 'ZipCode'),
        c('allotted', kind === 'sim' ? 'Alloted' : 'Allotted', 'number'),
        c('used', 'Used', 'number'),
        c('free', 'Free', 'number'),
        c('bad', 'Bad', 'number'),
        c('removed', 'Removed', 'number'),
      ],
    ),
    feature: 'inventory-reports-2',
  })),
  {
    ...inventoryReport(
      'unassigned-inventory',
      'Manage Unassigned Inventory',
      'Manage Unassigned Inventory',
      'PC306',
      [{ type: 'agentChain' }, { type: 'dateRange', key: 'date', label: 'Date Range' }],
      [
        c('master', 'Master Name C1'),
        c('c2', 'Total allotted to Master C2', 'number'),
        c('c3', 'Total Assigned to Distributor (M->D) C3', 'number'),
        c('c4', 'Total Assigned to Retailer (M->D->R) C4', 'number'),
        c('c5', 'Total Assigned to Employee (M->D->R->E) C5=(C7 + C8 + C9 + C10 + C11)', 'number'),
        c('c6', 'Total Not Assigned C6=(C2 - C3)', 'number'),
        c('c7', 'Total Free Phone C7', 'number'),
        c('c8', 'Total Used Phone C8', 'number'),
        c('c9', 'Total Bad Phone C9', 'number'),
        c('c10', 'Total Received Phone C10', 'number'),
        c('c11', 'Total Direct De-enroll Phone C11', 'number'),
      ],
    ),
    feature: 'inventory-reports-2',
  },
  {
    ...inventoryReport(
      'unassigned-device',
      'Manage Unassigned Device Inventory Report',
      'Manage Unassigned Device Inventory Report',
      'PC309',
      [{ type: 'agentChain' }, { type: 'dateRange', key: 'date', label: 'Date Range' }],
      [
        c('master', 'Master Name C1'),
        c('c2', 'Total allotted to Master C2', 'number'),
        c('c3', 'Total Assigned to Distributor (M->D) C3', 'number'),
        c('c4', 'Total Assigned to Retailer (M->D->R) C4=(C6 + C7 + C8)', 'number'),
        c('c5', 'Total Not Assigned C5=(C2 - C3)', 'number'),
        c('c6', 'Total Free Phone C6', 'number'),
        c('c7', 'Total Used Phone C7', 'number'),
        c('c8', 'Total Bad Phone C8', 'number'),
      ],
    ),
    feature: 'inventory-reports-2',
  },
  {
    ...inventoryReport(
      'sim-summary',
      'ESN Summary Report',
      'ESN Summary Report',
      'PC182',
      [],
      [
        c('stock', 'Stock', 'number'),
        c('free', "Free ESN's", 'number'),
        c('used', "Used ESN's", 'number'),
      ],
    ),
    feature: 'inventory-reports-2',
  },
  {
    ...inventoryReport(
      'unassigned-stock',
      'Inventory Unassigned Stock Report',
      'Inventory Unassigned Stock Report',
      'PC644',
      [
        { type: 'dateRange', key: 'date', label: 'Date Range' },
        { type: 'text', key: 'batch', label: 'Batch #', maxLength: 30 },
      ],
      [
        c('batch', 'Batch #'),
        c('fileName', 'File Name'),
        c('available', 'Total Available', 'number'),
        c('uploadedAt', 'Uploaded Date Time', 'datetime'),
        c('uploadedBy', 'Uploaded By'),
      ],
    ),
    feature: 'inventory-reports-2',
  },
  inventoryReport(
    'stock-complete',
    'Complete stock report',
    'Inventory Reports',
    'PC19',
    [{ type: 'carrier', key: 'carrier', label: 'Carrier' }],
    [
      c('product', 'Product'),
      c('kind', 'Type'),
      c('total', 'Total', 'number'),
      c('free', 'Available', 'number'),
      c('used', 'Used', 'number'),
      c('bad', 'Bad', 'number'),
      c('removed', 'Removed', 'number'),
      c('other', 'Others', 'number'),
    ],
  ),
  inventoryReport(
    'stock-provisioned',
    'Provisioned stock report',
    'Inventory Reports',
    'PC19',
    [{ type: 'carrier', key: 'carrier', label: 'Carrier' }, { type: 'agentChain' }],
    [
      c('kind', 'Type'),
      c('identifier', 'SIM / IMEI'),
      c('product', 'Product'),
      c('carrier', 'Carrier'),
      c('mdn', 'MDN'),
      c('customerId', 'Customer ID'),
      c('customerName', 'Customer Name'),
      c('store', 'Store ID'),
    ],
  ),
];

/* ------------- Agent Inventory / Device Report (PC307, PC308) --------------- */

/**
 * Reporting > Agent Inventory Report and Agent Device Report (walks of 2026-10-23 and 2026-10-27;
 * switch `reporting-2`): one line per unit of the agents chosen, with the customer who
 * holds it. Assumptions: "Current Inventory" is a unit no customer holds, "Handover Completed"
 * one a customer holds; "Provisioned" is a unit in use, as on the Provisioned stock report. The
 * reference's "Log Status" (Exclude Log, Include Log) is left out: what it changes was not seen.
 * The device report's results were not seen; it shows the same columns with the Device ID.
 */
export const AGENT_UNIT_SEARCH = {
  sim: ['All Inventory', 'Current Inventory', 'Handover Completed'],
  device: ['All Device', 'Current Device', 'Handover Completed'],
} as const;

const agentUnitReport = (kind: 'sim' | 'device'): ReportDef => ({
  key: kind === 'sim' ? 'agent-inventory-report' : 'agent-device-report',
  group: 'screen',
  name: kind === 'sim' ? 'Agent Inventory Report' : 'Agent Device Report',
  heading: kind === 'sim' ? 'Agent Inventory Report' : 'Agent Device Report',
  code: kind === 'sim' ? 'PC307' : 'PC308',
  feature: 'reporting-2',
  filters: [
    { type: 'agentChain' },
    { type: 'dateRange', key: 'date', label: 'Date Range' },
    { type: 'text', key: 'box', label: 'Enter Box Number', maxLength: 30 },
    { type: 'text', key: 'po', label: 'Enter PO Number', maxLength: 30 },
    {
      type: 'text',
      key: 'unit',
      label: kind === 'sim' ? 'Enter SIM' : 'Enter Device ID',
      maxLength: 30,
    },
    {
      type: 'select',
      key: 'search',
      label: kind === 'sim' ? 'Search Inventory' : 'Search Device',
      options: AGENT_UNIT_SEARCH[kind],
    },
    {
      type: 'select',
      key: 'status',
      label: kind === 'sim' ? 'SIM Status' : 'Device Status',
      options: ['Free', 'Used', 'Bad', 'Removed'],
      emptyLabel: 'All',
    },
    ...(kind === 'sim'
      ? [
          {
            type: 'select' as const,
            key: 'provision',
            label: 'Provision Status',
            options: ['Provisioned', 'Unprovisioned'],
            emptyLabel: 'All',
          },
        ]
      : []),
  ],
  columns: [
    c('customerId', 'Cust ID'),
    c('enrollmentId', 'Enrollment ID'),
    c('firstName', 'First Name'),
    c('lastName', 'Last Name'),
    c('address', 'Service Address'),
    c('city', 'City'),
    c('state', 'State'),
    c('zip', 'Zip'),
    c('unit', kind === 'sim' ? 'ESN Number' : 'Device ID'),
    c('telephone', 'Telephone'),
    c('enrollmentType', 'Enrollment Type'),
    c('status', 'Status'),
  ],
  submitLabel: 'Search',
});
const AGENT_UNIT_SCREENS: ReportDef[] = [agentUnitReport('sim'), agentUnitReport('device')];

/* ---------------------------- Coupon codes (PC852) ------------------------- */

/**
 * Coupon codes > View Report, "Coupon Code Usage Report" (walk of 2026-10-27): one line per use
 * of a coupon. The reference's "Category" select is left out (its choices were not seen). Device
 * Info is the IMEI of the customer's device; Amount Paid is what the invoice the coupon was used
 * on came to.
 */
const COUPON_SCREENS: ReportDef[] = [
  {
    key: 'coupon-usage',
    group: 'screen',
    name: 'Coupon Code Usage Report',
    heading: 'Coupon Code Usage Report',
    code: 'PC852',
    feature: 'coupon-usage-report',
    filters: [
      { type: 'text', key: 'couponCode', label: 'Coupon Code', maxLength: 40 },
      { type: 'text', key: 'mdn', label: 'MDN', maxLength: 10 },
      { type: 'dateRange', key: 'date', label: 'Date' },
      // "Category": what the coupon applies to (its Coupon Applicable On).
      {
        type: 'select',
        key: 'category',
        label: 'Category',
        options: ['Equipment', 'Plan', 'Activation Fee', 'Total Cost', 'Gift Card'],
        emptyLabel: 'All Categories',
        feature: 'reports-extras-3',
      },
    ],
    columns: [
      c('coupon', 'Coupon'),
      c('promotionName', 'Promotion Name'),
      c('appliedTo', 'Applied To'),
      c('amountDiscount', 'Amount Discount', 'money'),
      c('date', 'Date', 'datetime'),
      c('enrollmentId', 'Enrollment ID'),
      c('customerId', 'Customer ID'),
      c('firstName', 'First Name'),
      c('lastName', 'Last Name'),
      c('address', 'Address'),
      c('city', 'City'),
      c('state', 'State'),
      c('zip', 'Zip Code'),
      c('mdn', 'MDN'),
      c('plan', 'Plan'),
      c('deviceInfo', 'Device Info'),
      c('amountPaid', 'Amount Paid', 'money'),
      c('source', 'Source'),
      c('accountStatus', 'Account Status'),
    ],
    submitLabel: 'Submit',
  },
];

/* ------------------- Inventory Report (PC350): ten reports ------------------ */

/**
 * The ten reports listed on Inventory Report (walk of 2026-10-27, third round; release switch
 * `inventory-report-subreports`). Filters and columns are the reference's less what 212Ez does
 * not keep (BYOD, Vendor, Tracking Number, Plan Type, provisioning states). The rows of the
 * Agent Wise and Snapshot reports count 212Ez's statuses; which statuses each row counts is
 * said in the row (an assumption: the reference counts its own provisioning states).
 */
export const INVENTORY_SUBREPORTS_FEATURE = 'inventory-report-subreports';
const subReport = (
  key: string,
  name: string,
  code: string,
  filters: ReportFilter[],
  columns: ReportColumn[],
  submitLabel: ReportDef['submitLabel'] = 'Generate Report',
): ReportDef => ({
  key,
  group: 'screen',
  name,
  heading: name,
  code,
  feature: INVENTORY_SUBREPORTS_FEATURE,
  filters,
  columns,
  submitLabel,
  resultHeading: name,
});
const AGENT_STATUS_FILTER: ReportFilter = {
  type: 'select',
  key: 'agentStatus',
  label: 'Agent Status',
  options: ['Free', 'Used', 'Removed', 'Bad'],
  multiple: true,
};
const COUNT_ROWS = [
  c('agent', 'Agent'),
  c('category', 'Inventory'),
  c('states', 'Statuses counted'),
  c('count', 'Count', 'number'),
];
const BY_AGENT_COLUMNS = [
  c('masterId', 'Master ID'),
  c('masterName', 'Master Name'),
  c('allotted', 'Allotted', 'number'),
  c('used', 'Used', 'number'),
  c('free', 'Free', 'number'),
  c('bad', 'Bad', 'number'),
  c('removed', 'Removed', 'number'),
];
const CHAIN_COLUMNS = [
  c('masterAgent', 'Master Agent'),
  c('distributor', 'Distributor'),
  c('retailer', 'Retailer'),
  c('employee', 'Employee'),
];
const CUSTOMER_COLUMNS = [
  c('customerId', 'Cust ID'),
  c('enrollmentId', 'Enrollment ID'),
  c('customerStatus', 'Cust Status'),
  c('uploadedBy', 'Uploaded by'),
  c('uploadedAt', 'Uploaded Date', 'datetime'),
  c('previousStatus', 'Previous Status'),
  c('previousStatusAt', 'Previous Status Change Date', 'datetime'),
];
const TRANSACTION_COLUMNS = [
  c('date', 'Date', 'datetime'),
  c('unit', 'SIM / Device'),
  c('transaction', 'Transaction'),
  c('from', 'From'),
  c('to', 'To'),
  c('customerId', 'Customer ID'),
  c('by', 'By'),
];

export const INVENTORY_SUBREPORTS: ReportDef[] = [
  subReport(
    'agent-wise-sim-inventory',
    'Agent Wise Inventory Report (SIM)',
    'PC487',
    [{ type: 'agentChain' }],
    COUNT_ROWS,
  ),
  subReport(
    'sim-inventory-by-agent',
    'SIM Inventory By Agent',
    'PC502',
    [{ type: 'agentChain' }],
    BY_AGENT_COLUMNS,
    'Search',
  ),
  subReport(
    'sim-inventory-detail',
    'SIM Inventory Detail',
    'PC489',
    [
      { type: 'agentChain' },
      { type: 'dateRange', key: 'uploaded', label: 'Uploaded Date' },
      { type: 'text', key: 'unit', label: 'SIM / Device', maxLength: 30 },
      { type: 'carrier', key: 'carrier', label: 'Carrier' },
      AGENT_STATUS_FILTER,
    ],
    [
      c('unit', 'SIM'),
      c('mdn', 'MDN'),
      c('tagged', 'Tagged Device'),
      c('uiccid', 'UICCID'),
      c('status', 'Agent Status'),
      c('carrier', 'Carrier'),
      c('makeModel', 'Make Model'),
      c('po', 'PO'),
      c('sellingPrice', 'Cost (Selling Price)', 'money'),
      c('wholesalePrice', 'Wholesale Price', 'money'),
      c('assignedAt', 'Assign Date', 'datetime'),
      ...CHAIN_COLUMNS,
      ...CUSTOMER_COLUMNS,
    ],
    'Search',
  ),
  // Shown on opening, as on the reference.
  {
    ...subReport(
      'sim-inventory-snapshot',
      'Inventory Snapshot (SIM)',
      'PC488',
      [],
      COUNT_ROWS.slice(1),
    ),
    autoRun: true,
  },
  subReport(
    'sim-inventory-transactions',
    'Transaction Inventory Report (SIM)',
    'PC486',
    [
      { type: 'text', key: 'mdn', label: 'MDN', maxLength: 10 },
      { type: 'text', key: 'sim', label: 'SIM', maxLength: 30 },
      { type: 'text', key: 'customerId', label: 'Customer ID', maxLength: 12 },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
    ],
    TRANSACTION_COLUMNS,
  ),
  subReport(
    'agent-device-inventory-report',
    'Agent Device Inventory',
    'PC491',
    [{ type: 'agentChain' }],
    COUNT_ROWS,
  ),
  subReport(
    'device-by-agent',
    'Device By Agent',
    'PC501',
    [{ type: 'agentChain' }],
    BY_AGENT_COLUMNS,
    'Search',
  ),
  subReport(
    'device-inventory-detail',
    'Device Inventory Detail',
    'PC493',
    [
      { type: 'agentChain' },
      { type: 'dateRange', key: 'uploaded', label: 'Uploaded Date' },
      { type: 'text', key: 'unit', label: 'SIM / Device', maxLength: 30 },
      AGENT_STATUS_FILTER,
    ],
    [
      c('unit', 'Device'),
      c('tagged', 'Tagged SIM'),
      c('status', 'Status'),
      c('makeModel', 'Make Model'),
      c('po', 'PO Number'),
      c('sellingPrice', 'Selling Price', 'money'),
      c('wholesalePrice', 'Wholesale Cost', 'money'),
      c('acpRate', 'ACP Expected Rate', 'money'),
      c('acpCopay', 'ACP Co Pay Amount', 'money'),
      c('assignedBy', 'Assign By'),
      c('assignedAt', 'Assign Date', 'datetime'),
      ...CHAIN_COLUMNS,
      ...CUSTOMER_COLUMNS,
    ],
    'Search',
  ),
  // Shown on opening, as on the reference.
  {
    ...subReport(
      'device-inventory-snapshot',
      'Inventory Device Snapshot',
      'PC492',
      [],
      COUNT_ROWS.slice(1),
    ),
    autoRun: true,
  },
  subReport(
    'device-inventory-transactions',
    'Transaction Device Inventory Report',
    'PC490',
    [
      { type: 'text', key: 'device', label: 'Device', maxLength: 30 },
      { type: 'text', key: 'sim', label: 'SIM', maxLength: 30 },
      { type: 'text', key: 'customerId', label: 'Customer ID', maxLength: 12 },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
    ],
    TRANSACTION_COLUMNS,
  ),
];

/* ---------------------- Reporting (PC19), walk of 2026-10-28 --------------------- */

const r3 = (def: Omit<ReportDef, 'group' | 'submitLabel' | 'autoRun' | 'feature'>): ReportDef => ({
  ...def,
  group: 'screen',
  feature: 'reporting-3',
  submitLabel: 'Search',
  autoRun: true,
});

const BATCH_COLUMNS = (kind: 'sim' | 'device'): ReportColumn[] => [
  c('batch', 'Batch #'),
  c('fileName', kind === 'sim' ? 'Uploaded File Name' : 'File Name'),
  c('total', kind === 'sim' ? 'Total Uploaded' : 'Total Uploaded Device ID', 'number'),
  c('success', kind === 'sim' ? 'Total Success' : 'Total Success Device ID', 'number'),
  c('failure', kind === 'sim' ? 'Total Failure' : 'Total Failure Device ID', 'number'),
  c('uploadedAt', 'Uploaded Date Time', 'datetime'),
  c('uploadedBy', 'Uploaded By'),
  c('status', 'Status'),
];

const BOX_COLUMNS: ReportColumn[] = [
  c('box', 'Box ID'),
  c('createdAt', 'Tracking Date', 'datetime'),
  c('createdBy', 'Box Created By'),
  c('size', 'Box Size', 'number'),
  c('status', 'Box Status'),
];

/**
 * The Reporting links of the walk of 2026-10-28 (switch `reporting-3`). The batch reports list the
 * uploads of Add Bulk Unit; the box reports group units by the Box ID set on them (Update
 * Inventory), since 212Ez has no box-creating screen; the swap report reads the ESN/SIM changes.
 * Where the reference showed no rows, the columns are assumptions, said beside each.
 */
const REPORTING_3_SCREENS: ReportDef[] = [
  r3({
    key: 'sim-batch',
    name: 'SIM Batch Report',
    heading: 'SIM Batch Report',
    code: 'PC187',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Start To End Date' },
      { type: 'text', key: 'batch', label: 'Batch #', maxLength: 30 },
    ],
    columns: BATCH_COLUMNS('sim'),
  }),
  r3({
    key: 'device-batch',
    name: 'Device Report',
    heading: 'DEVICE ID Report',
    code: 'PC185',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Start To End Date' },
      { type: 'text', key: 'batch', label: 'Batch #', maxLength: 30 },
    ],
    columns: BATCH_COLUMNS('device'),
  }),
  r3({
    key: 'universal-inventory',
    name: 'Universal Inventory Reports',
    heading: 'Universal Inventory Report',
    code: 'PC508',
    filters: [
      { type: 'agentChain' },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      { type: 'select', key: 'searchBy', label: 'Search By Report', options: ['SIM', 'Box ID'] },
      { type: 'text', key: 'q', label: 'SIM OR Box ID', maxLength: 30 },
      { type: 'text', key: 'model', label: 'Search By Model', maxLength: 60 },
      {
        type: 'select',
        key: 'phoneType',
        label: 'Phone Type',
        options: ['SIM', 'Device'],
        multiple: true,
      },
      {
        type: 'select',
        key: 'status',
        label: 'Phone Status',
        options: ['Free', 'Used', 'Bad', 'Removed', 'Unassigned'],
        multiple: true,
      },
      { type: 'carrier', key: 'carrier', label: 'Phone Carrier' },
    ],
    // Assumption: the reference showed no rows, so these columns are 212Ez's.
    columns: [
      c('unit', 'SIM / Device ID'),
      c('kind', 'Phone Type'),
      c('box', 'Box ID'),
      c('model', 'Model'),
      c('carrier', 'Carrier'),
      c('status', 'Phone Status'),
      c('agent', 'Agent'),
      c('uploadedAt', 'Uploaded Date', 'datetime'),
    ],
  }),
  r3({
    key: 'vendor-summary',
    name: 'Vendor Summary',
    heading: 'Vendor Summary Report',
    code: 'PC183',
    // Assumption: 212Ez keeps no vendors, so the summary is by model, as the reference's only filter is.
    filters: [{ type: 'text', key: 'model', label: 'Model', maxLength: 60 }],
    columns: [
      c('model', 'Model'),
      c('kind', 'Type'),
      c('total', 'Total', 'number'),
      c('free', 'Free', 'number'),
      c('used', 'Used', 'number'),
      c('bad', 'Bad', 'number'),
      c('removed', 'Removed', 'number'),
    ],
  }),
  r3({
    key: 'sim-boxes',
    name: 'Ship SIM to Agent (Store) By Creating Boxes',
    heading: 'Ship SIM to Agent (Store) By Creating Boxes : View Report',
    code: 'PC477',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Start To End Date' },
      { type: 'text', key: 'box', label: 'Box ID', maxLength: 30 },
    ],
    columns: BOX_COLUMNS,
  }),
  r3({
    key: 'device-boxes',
    name: 'Ship Device to Agent (Store) By Creating Boxes',
    heading: 'Ship Device to Agent (Store) By Creating Boxes : View Report',
    code: 'PC478',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Start To End Date' },
      { type: 'text', key: 'box', label: 'Box ID', maxLength: 30 },
    ],
    columns: BOX_COLUMNS,
  }),
  r3({
    key: 'sim-swap',
    name: 'SIM/UICCID Swap Report',
    heading: 'ESN/SIM/UICCID Swap Report',
    code: 'PC656',
    filters: [
      { type: 'text', key: 'customerId', label: 'Customer ID', maxLength: 12 },
      { type: 'text', key: 'mdn', label: 'MDN', maxLength: 10 },
      { type: 'text', key: 'sim', label: 'SIM', maxLength: 30 },
      {
        type: 'select',
        key: 'processType',
        label: 'Process Type',
        options: ['CHANGE ESN', 'BOTH'],
        emptyLabel: '----- Select Process Type -----',
      },
      { type: 'dateRange', key: 'date', label: 'Start To End Date' },
    ],
    // The reference's columns that 212Ez keeps; UICCIDs, device types, balances and the carrier's
    // swap messages are not recorded on a SIM change.
    columns: [
      c('customerId', 'Customer ID'),
      c('oldSim', 'Old SIM'),
      c('newSim', 'New SIM'),
      c('mdn', 'MDN'),
      c('carrier', 'Carrier'),
      c('processType', 'Process Type'),
      c('reason', 'Reason'),
      c('source', 'Source'),
      c('changeBy', 'Change By'),
      c('changeDate', 'Change Date', 'datetime'),
    ],
  }),
];

/* ------------------------------- Registration ------------------------------ */

export const SCREEN_DEFS: readonly ReportDef[] = [
  ...PORT_SCREENS,
  ...PORTOUT_SCREENS,
  ...SHIPMENT_SCREENS,
  ...REVIEW_SCREENS,
  ...CUSTOMER_SERVICE_SCREENS,
  ...INVENTORY_SCREENS,
  ...COUPON_SCREENS,
  ...AGENT_UNIT_SCREENS,
  ...INVENTORY_SUBREPORTS,
  ...REPORTING_3_SCREENS,
];
registerReportDefs(SCREEN_DEFS);
